import { CompType } from '@aloft/toolkit-shared';
import { Text, View, ViewProps } from '@react-pdf/renderer';

import { PropsWithChildren } from 'react';

import { tableStyles } from '../styles.ts';
import { CompTypeBadge } from './CompTypeBadge.tsx';

const styleToArray = (style: ViewProps['style']) =>
  (style && (Array.isArray(style) ? style : [style])) ?? [];

const Column = ({ children, style }: PropsWithChildren<{ style?: ViewProps['style'] }>) => (
  <View style={[tableStyles.column, ...styleToArray(style)]}>{children}</View>
);

type CellProps = PropsWithChildren<{ compact?: boolean; style?: ViewProps['style'] }>;

export const Cell = ({ children, compact, style }: CellProps) => (
  <View style={[compact ? tableStyles.compactCell : tableStyles.cell, ...styleToArray(style)]}>
    {children}
  </View>
);

const TableText = ({ children }: PropsWithChildren<unknown>) => (
  <Text style={tableStyles.text}>{children}</Text>
);

const TextCell = ({ children, ...cellProps }: CellProps) => (
  <Cell {...cellProps}>
    <TableText>{children}</TableText>
  </Cell>
);

const CompTypeCell = ({
  children,
  compact,
  compType,
  style,
}: CellProps & { compType?: CompType }) => (
  <Cell
    compact
    style={[
      {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      },
      ...styleToArray(style),
    ]}
  >
    <TableText>{children}</TableText>
    {compType && <CompTypeBadge compType={compType} compact={compact} />}
  </Cell>
);

const Table = ({ children }: PropsWithChildren<unknown>) => (
  <View wrap={false} style={tableStyles.table}>
    {children}
  </View>
);

Table.Column = Column;
Table.Cell = Cell;
Table.Text = TableText;
Table.TextCell = TextCell;
Table.CompTypeCell = CompTypeCell;

export { Table };
